/**
 * JudgmentProfileForm — Pattern A modal to record or edit one Judgment Index
 * result. The practitioner enters the 6 primary IES scores (Part 1 Work Side /
 * Part 2 Self Side), the 6 Balance of Judgment meter values, and the Primary
 * Judgment Type number and name, exactly as they appear on the official Score Sheet.
 *
 * The platform does not compute scores — this is transcription of results
 * produced by a certified Judgment Index assessment.
 */

import { useState } from 'react';
import { X, Scale, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import type {
  JudgmentIES,
  JudgmentIESBands,
  JudgmentBalance,
  JudgmentIndexProfile,
  JudgmentProfileInput,
  ScoreBand,
} from '../../../types/prioritization/judgmentIndex';
import { BAND_META } from '../../../types/prioritization/judgmentIndex';
import { emptyIES, emptyBalance, emptyBands } from '../../../lib/judgmentIndexModel';

const BAND_OPTIONS: { value: ScoreBand; label: string }[] = [
  { value: 'strong',     label: BAND_META.strong.label     },
  { value: 'good',       label: BAND_META.good.label       },
  { value: 'attention',  label: BAND_META.attention.label  },
  { value: 'developing', label: BAND_META.developing.label },
  { value: 'needs_work', label: BAND_META.needs_work.label },
];

function BandSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ScoreBand | null;
  onChange: (v: ScoreBand | null) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[var(--text-sec)] mb-0.5">{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange((e.target.value as ScoreBand) || null)}
        className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)]"
      >
        <option value="">— not recorded —</option>
        {BAND_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function hasBands(b: JudgmentIESBands): boolean {
  return b.intrinsic !== null || b.extrinsic !== null || b.systemic !== null;
}

function NumInput({
  label,
  subLabel,
  value,
  onChange,
  min = 0,
  max,
}: {
  label: string;
  subLabel?: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[var(--text-sec)] mb-0.5">{label}</label>
      {subLabel && <p className="text-[11px] text-[var(--text-ter)] leading-tight mb-1">{subLabel}</p>}
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isNaN(n)) onChange(Math.max(min, max !== undefined ? Math.min(max, n) : n));
        }}
        className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)] tabular-nums"
      />
    </div>
  );
}

export function JudgmentProfileForm({
  existing,
  onClose,
  onSubmit,
}: {
  existing?: JudgmentIndexProfile | null;
  onClose: () => void;
  onSubmit: (input: JudgmentProfileInput) => Promise<{ error?: string }>;
}) {
  const [subjectName,   setSubjectName]   = useState(existing?.subjectName   ?? '');
  const [subjectEmail,  setSubjectEmail]  = useState(existing?.subjectEmail  ?? '');
  const [assessedOn,    setAssessedOn]    = useState(existing?.assessedOn    ?? '');
  const [notes,         setNotes]         = useState(existing?.notes         ?? '');
  const [judgmentType,  setJudgmentType]  = useState<number>(existing?.judgmentType ?? 0);
  const [judgmentTypeName, setJudgmentTypeName] = useState(existing?.judgmentTypeName ?? '');
  const [workSide, setWorkSide] = useState<JudgmentIES>(existing?.workSide ?? emptyIES());
  const [selfSide, setSelfSide] = useState<JudgmentIES>(existing?.selfSide ?? emptyIES());
  const [balance,  setBalance]  = useState<JudgmentBalance>(existing?.balance ?? emptyBalance());
  const [workSideBands, setWorkSideBands] = useState<JudgmentIESBands>(existing?.workSideBands ?? emptyBands());
  const [selfSideBands, setSelfSideBands] = useState<JudgmentIESBands>(existing?.selfSideBands ?? emptyBands());
  const [showBalance, setShowBalance] = useState(
    existing ? Object.values(existing.balance ?? {}).some((v) => v !== 0) : false,
  );
  const [showBands, setShowBands] = useState(
    existing ? hasBands(existing.workSideBands ?? emptyBands()) || hasBands(existing.selfSideBands ?? emptyBands()) : false,
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  function patchWork(k: keyof JudgmentIES, v: number) {
    setWorkSide((prev) => ({ ...prev, [k]: v }));
  }
  function patchSelf(k: keyof JudgmentIES, v: number) {
    setSelfSide((prev) => ({ ...prev, [k]: v }));
  }
  function patchBalance(k: keyof JudgmentBalance, v: number) {
    setBalance((prev) => ({ ...prev, [k]: v }));
  }
  function patchWorkBand(k: keyof JudgmentIESBands, v: ScoreBand | null) {
    setWorkSideBands((prev) => ({ ...prev, [k]: v }));
  }
  function patchSelfBand(k: keyof JudgmentIESBands, v: ScoreBand | null) {
    setSelfSideBands((prev) => ({ ...prev, [k]: v }));
  }

  async function handleSave() {
    if (!subjectName.trim()) { setError('A name is required.'); return; }
    setSaving(true);
    setError(null);
    const result = await onSubmit({
      subjectUserId:    existing?.subjectUserId ?? null,
      subjectName:      subjectName.trim(),
      subjectEmail:     subjectEmail.trim() || null,
      assessedOn:       assessedOn || null,
      source:           existing?.source ?? 'manual',
      workSide,
      selfSide,
      balance,
      workSideBands: hasBands(workSideBands) ? workSideBands : null,
      selfSideBands: hasBands(selfSideBands) ? selfSideBands : null,
      judgmentType:     judgmentType > 0 ? judgmentType : null,
      judgmentTypeName: judgmentTypeName.trim() || null,
      notes:            notes.trim() || null,
    });
    setSaving(false);
    if (result.error) { setError(result.error); return; }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80 overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-[var(--slate-dim)] flex items-center justify-center">
              <Scale className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </div>
            <h2 className="text-base font-semibold text-[var(--text-pri)]">
              {existing ? 'Edit Judgment Index profile' : 'Record Judgment Index profile'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto">

          {/* Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
                Name <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                value={subjectName}
                onChange={(e) => setSubjectName(e.target.value)}
                placeholder="Person assessed"
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">Email <span className="text-[var(--text-ter)] font-normal">(optional)</span></label>
              <input
                value={subjectEmail}
                onChange={(e) => setSubjectEmail(e.target.value)}
                placeholder="name@company.com"
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">Assessed on <span className="text-[var(--text-ter)] font-normal">(optional)</span></label>
              <input
                type="date"
                value={assessedOn}
                onChange={(e) => setAssessedOn(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)] text-[var(--text-sec)]"
              />
            </div>
          </div>

          {/* Part 1 — Work Side */}
          <div className="border-t border-[var(--rule)] pt-4">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">
              Part 1 — Work Side (Primary Judgment Indicators)
            </p>
            <div className="grid grid-cols-3 gap-3">
              <NumInput
                label="People / Relational"
                subLabel="Relational Insight (I)"
                value={workSide.intrinsic}
                onChange={(v) => patchWork('intrinsic', v)}
              />
              <NumInput
                label="Work / Tactical"
                subLabel="Tactical Insight (E)"
                value={workSide.extrinsic}
                onChange={(v) => patchWork('extrinsic', v)}
              />
              <NumInput
                label="Big Picture / Strategic"
                subLabel="Strategic Insight (S)"
                value={workSide.systemic}
                onChange={(v) => patchWork('systemic', v)}
              />
            </div>
          </div>

          {/* Part 2 — Self Side */}
          <div className="border-t border-[var(--rule)] pt-4">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">
              Part 2 — Self Side (Primary Judgment Indicators)
            </p>
            <div className="grid grid-cols-3 gap-3">
              <NumInput
                label="Self-Worth / Intrinsic"
                subLabel="Intrinsic Self (I)"
                value={selfSide.intrinsic}
                onChange={(v) => patchSelf('intrinsic', v)}
              />
              <NumInput
                label="Role Clarity / Practical"
                subLabel="Extrinsic Self (E)"
                value={selfSide.extrinsic}
                onChange={(v) => patchSelf('extrinsic', v)}
              />
              <NumInput
                label="Self-Direction / Systemic"
                subLabel="Systemic Self (S)"
                value={selfSide.systemic}
                onChange={(v) => patchSelf('systemic', v)}
              />
            </div>
          </div>

          {/* Balance of Judgment */}
          <div className="border-t border-[var(--rule)] pt-4">
            <button
              type="button"
              onClick={() => setShowBalance((b) => !b)}
              className="flex items-center gap-2 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide w-full text-left"
            >
              {showBalance ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Balance of Judgment (−25 to +25)
            </button>
            {showBalance && (
              <div className="mt-3 space-y-3">
                <p className="text-[11px] text-[var(--text-ter)]">Proper balance: −5 to +5. Enter the meter values from the score sheet.</p>
                <div className="grid grid-cols-3 gap-3">
                  <NumInput label="People / Relationships" value={balance.people}  onChange={(v) => patchBalance('people',  v)} min={-25} max={25} />
                  <NumInput label="Work / Tasks"           value={balance.work}    onChange={(v) => patchBalance('work',    v)} min={-25} max={25} />
                  <NumInput label="Balance with Ideas"     value={balance.ideas}   onChange={(v) => patchBalance('ideas',   v)} min={-25} max={25} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <NumInput label="Self-Esteem / Confidence" value={balance.esteem}  onChange={(v) => patchBalance('esteem',  v)} min={-25} max={25} />
                  <NumInput label="Self-Concept / Role"      value={balance.concept} onChange={(v) => patchBalance('concept', v)} min={-25} max={25} />
                  <NumInput label="Self-Image / Motivation"  value={balance.image}   onChange={(v) => patchBalance('image',   v)} min={-25} max={25} />
                </div>
              </div>
            )}
          </div>

          {/* Band labels from report */}
          <div className="border-t border-[var(--rule)] pt-4">
            <button
              type="button"
              onClick={() => setShowBands((b) => !b)}
              className="flex items-center gap-2 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide w-full text-left"
            >
              {showBands ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Band labels from report <span className="normal-case font-normal text-[var(--text-ter)]">(optional)</span>
            </button>
            {showBands && (
              <div className="mt-3 space-y-3">
                <p className="text-[11px] text-[var(--text-ter)]">
                  Enter the band label shown on the report for each dimension — these override the computed
                  band when displayed, so your assessment always reflects the final result.
                </p>
                <p className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Part 1 — Work Side</p>
                <div className="grid grid-cols-3 gap-3">
                  <BandSelect label="People / Relational" value={workSideBands.intrinsic} onChange={(v) => patchWorkBand('intrinsic', v)} />
                  <BandSelect label="Work / Tactical"     value={workSideBands.extrinsic} onChange={(v) => patchWorkBand('extrinsic', v)} />
                  <BandSelect label="Big Picture"         value={workSideBands.systemic}  onChange={(v) => patchWorkBand('systemic',  v)} />
                </div>
                <p className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Part 2 — Self Side</p>
                <div className="grid grid-cols-3 gap-3">
                  <BandSelect label="Self-Worth"      value={selfSideBands.intrinsic} onChange={(v) => patchSelfBand('intrinsic', v)} />
                  <BandSelect label="Role Clarity"    value={selfSideBands.extrinsic} onChange={(v) => patchSelfBand('extrinsic', v)} />
                  <BandSelect label="Self-Direction"  value={selfSideBands.systemic}  onChange={(v) => patchSelfBand('systemic',  v)} />
                </div>
              </div>
            )}
          </div>

          {/* Primary Judgment Type */}
          <div className="border-t border-[var(--rule)] pt-4">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">
              Primary Judgment Type <span className="normal-case font-normal text-[var(--text-ter)]">(optional)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">Type number (1–13)</label>
                <input
                  type="number"
                  min={1}
                  max={13}
                  value={judgmentType || ''}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setJudgmentType(Number.isNaN(n) ? 0 : Math.max(0, Math.min(13, Math.round(n))));
                  }}
                  placeholder="e.g. 4"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)] tabular-nums"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">Type name (from report)</label>
                <input
                  value={judgmentTypeName}
                  onChange={(e) => setJudgmentTypeName(e.target.value)}
                  placeholder="e.g. Counseling/Coaching/Mentoring"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)]"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="border-t border-[var(--rule)] pt-4">
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              Notes <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Practitioner notes, context, or caveats."
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] resize-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)]"
            />
          </div>

          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--rule)]">
          <button type="button" onClick={onClose} className="px-3.5 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] disabled:opacity-50 shadow-sm shadow-indigo-200 transition-all"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {existing ? 'Save changes' : 'Record profile'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default JudgmentProfileForm;
